/**
 * Posição do botão de ação. O backend guarda âncora e offsets numa string só:
 * `"S_Meio v50px h-30px"`. Save e os dois loads usam estes helpers para não
 * divergirem no parse — foi assim que os offsets já se perderam antes.
 */

import type { ActionButton } from '@/store/videoFeatures/types/overlay-features';

type PositionKey = NonNullable<ActionButton['position']>;

const FROM_BACKEND: Record<string, PositionKey> = {
  S_Esquerda: 'top-left',
  S_Meio: 'top-center',
  S_Direita: 'top-right',
  M_Esquerda: 'center-left',
  M_Meio: 'center',
  M_Direita: 'center-right',
  I_Esquerda: 'bottom-left',
  I_Meio: 'bottom-center',
  I_Direita: 'bottom-right',
};

const TO_BACKEND: Record<PositionKey, string> = {
  'top-left': 'S_Esquerda',
  'top-center': 'S_Meio',
  'top-right': 'S_Direita',
  'center-left': 'M_Esquerda',
  center: 'M_Meio',
  'center-right': 'M_Direita',
  'bottom-left': 'I_Esquerda',
  'bottom-center': 'I_Meio',
  'bottom-right': 'I_Direita',
};

export interface ParsedActionButtonPosition {
  position: PositionKey;
  horizontalOffset: number;
  verticalOffset: number;
}

/** Lê `"S_Meio v50px h-30px"`. Tolera string vazia, ausente ou só a âncora. */
export function parseActionButtonPosition(raw: string | undefined | null): ParsedActionButtonPosition {
  const parts = (raw || '').split(' ');
  let horizontalOffset = 0;
  let verticalOffset = 0;

  for (const part of parts.slice(1)) {
    const vertical = part.match(/^v(-?\d+)px$/);
    const horizontal = part.match(/^h(-?\d+)px$/);
    if (vertical) verticalOffset = parseInt(vertical[1], 10);
    if (horizontal) horizontalOffset = parseInt(horizontal[1], 10);
  }

  return {
    position: FROM_BACKEND[parts[0]] || 'top-center',
    horizontalOffset,
    verticalOffset,
  };
}

/** Monta a string do backend. Offset 0 sai da string, como o formato original. */
export function formatActionButtonPosition(
  position: ActionButton['position'],
  horizontalOffset = 0,
  verticalOffset = 0,
): string {
  const anchor = TO_BACKEND[(position as PositionKey) || 'top-center'] || 'S_Meio';
  const vertical = verticalOffset !== 0 ? ` v${verticalOffset}px` : '';
  const horizontal = horizontalOffset !== 0 ? ` h${horizontalOffset}px` : '';
  return `${anchor}${vertical}${horizontal}`;
}
